import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "data-table" }
const _hoisted_2 = { class: "data-table__header" }
const _hoisted_3 = { class: "data-table__title" }
const _hoisted_4 = {
  key: 0,
  class: "data-table__search"
}
const _hoisted_5 = {
  key: 0,
  class: "data-table__loading"
}
const _hoisted_6 = {
  key: 1,
  class: "data-table__wrapper"
}
const _hoisted_7 = { class: "data-table__table" }
const _hoisted_8 = { class: "data-table__thead" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "data-table__th-content" }
const _hoisted_11 = {
  key: 0,
  class: "data-table__sort-icon"
}
const _hoisted_12 = {
  key: 1,
  class: "icon icon-sort"
}
const _hoisted_13 = { class: "data-table__tbody" }
const _hoisted_14 = {
  key: 0,
  class: "data-table__td data-table__td--actions"
}
const _hoisted_15 = {
  key: 0,
  class: "data-table__empty"
}
const _hoisted_16 = ["colspan"]
const _hoisted_17 = { class: "text-muted" }
const _hoisted_18 = {
  key: 2,
  class: "data-table__pagination"
}
const _hoisted_19 = { class: "data-table__pagination-info" }
const _hoisted_20 = { class: "data-table__pagination-controls" }
const _hoisted_21 = ["disabled"]
const _hoisted_22 = { class: "data-table__pagination-current" }
const _hoisted_23 = ["disabled"]

import { ref, computed, watch } from 'vue';
import type { DataTableColumn, DataTableRow, SortDirection } from './types';
import { dataTableFormatters } from './types';
import ActionMenu from '@shell/components/ActionMenuShell.vue';
import { useStore } from 'vuex';

interface Props {
  columns: DataTableColumn[];
  rows: DataTableRow[];
  rowsPerPage?: number;
  searchable?: boolean;
  sortable?: boolean;
  paginated?: boolean;
  loading?: boolean;
  keyField?: string;
  rowActions?: boolean;
  rowActionsWidth?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DataTable',
  props: {
    columns: {},
    rows: {},
    rowsPerPage: { default: 10 },
    searchable: { type: Boolean, default: true },
    sortable: { type: Boolean, default: true },
    paginated: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    keyField: { default: 'id' },
    rowActions: { type: Boolean, default: true },
    rowActionsWidth: { default: 40 }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

// Define slot types - using Record<string, any> for row to allow dynamic properties


// State
const searchQuery = ref('');
const currentPage = ref(1);
const sortColumn = ref<string | null>(null);
const sortDirection = ref<SortDirection>('asc');
const store = useStore();

// Apply namespace filter first
const namespaceFilteredRows = computed<DataTableRow[]>(() => {
  // Access the cache key to trigger namespace filter changes
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cacheKey = store.state.activeNamespaceCacheKey;
  const activeNamespaces = store.state.activeNamespaceCache;

  // If no namespace filter exists, or if it's empty (no namespaces loaded yet), show all rows
  if (!activeNamespaces || Object.keys(activeNamespaces).length === 0) {
    return props.rows;
  }

  // Filter rows by namespace
  return props.rows.filter((row: any) => {
    const namespace = row.meta?.namespace;
    // If row has no namespace, show it by default
    if (!namespace) {
      return true;
    }
    // Check if this namespace is in the active filter
    return activeNamespaces[namespace];
  });
});

// Computed: Filtered rows based on search
const filteredRows = computed<DataTableRow[]>(() => {
  if (!searchQuery.value || !props.searchable) {
    return namespaceFilteredRows.value;
  }

  const query = searchQuery.value.toLowerCase();
  return namespaceFilteredRows.value.filter(row => {
    return props.columns.some(column => {
      if (column.searchable === false) {
        return false;
      }
      const value = getNestedValue(row, column.field);
      return String(value).toLowerCase().includes(query);
    });
  });
});

// Computed: Sorted rows
const sortedRows = computed<DataTableRow[]>(() => {
  if (!sortColumn.value || !props.sortable) {
    return filteredRows.value;
  }

  const column = props.columns.find(col => col.field === sortColumn.value);
  if (!column || column.sortable === false) {
    return filteredRows.value;
  }

  return [...filteredRows.value].sort((a, b) => {
    const aValue = getNestedValue(a, sortColumn.value!);
    const bValue = getNestedValue(b, sortColumn.value!);

    // Handle custom sort function
    if (column.sortFn) {
      return column.sortFn(a, b, sortDirection.value);
    }

    // Default sorting logic
    let comparison = 0;
    if (aValue < bValue) {
      comparison = -1;
    } else if (aValue > bValue) {
      comparison = 1;
    }

    return sortDirection.value === 'asc' ? comparison : -comparison;
  });
});

// Computed: Paginated rows
const paginatedRows = computed<DataTableRow[]>(() => {
  if (!props.paginated) {
    return sortedRows.value;
  }

  const start = (currentPage.value - 1) * props.rowsPerPage;
  const end = start + props.rowsPerPage;
  return sortedRows.value.slice(start, end);
});

// Computed: Total pages
const totalPages = computed(() => {
  if (!props.paginated) {
    return 1;
  }
  return Math.ceil(sortedRows.value.length / props.rowsPerPage);
});

// Computed: Pagination info
const paginationInfo = computed(() => {
  const start = (currentPage.value - 1) * props.rowsPerPage + 1;
  const end = Math.min(currentPage.value * props.rowsPerPage, sortedRows.value.length);
  return {
    start,
    end,
    total: sortedRows.value.length
  };
});

// Methods
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

function formatValue(row: DataTableRow, column: DataTableColumn): any {
  const value = getNestedValue(row, column.field);

  if (column.formatter) {
    // If formatter is a string, use dataTableFormatters
    if (typeof column.formatter === 'string') {
      const formatter = dataTableFormatters[column.formatter as keyof typeof dataTableFormatters];
      if (formatter) {
        return formatter(value);
      }
    } else {
      // Otherwise, use custom formatter function
      return column.formatter(value, row);
    }
  }

  return value;
}

function handleSort(columnField: string) {
  const column = props.columns.find(col => col.field === columnField);
  if (!column || column.sortable === false || !props.sortable) {
    return;
  }

  if (sortColumn.value === columnField) {
    // Toggle direction
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    // New column, default to ascending
    sortColumn.value = columnField;
    sortDirection.value = 'asc';
  }
}

function goToPage(page: number) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
}

function nextPage() {
  goToPage(currentPage.value + 1);
}

function prevPage() {
  goToPage(currentPage.value - 1);
}

function getRowKey(row: DataTableRow, index: number): string {
  return row[props.keyField] || `row-${index}`;
}

// Watch: Reset to page 1 when search or sort changes
watch([searchQuery, sortColumn, sortDirection], () => {
  currentPage.value = 1;
});

// Expose methods for parent components
__expose({
  goToPage,
  nextPage,
  prevPage,
  resetSearch: () => { searchQuery.value = ''; },
  resetSort: () => { sortColumn.value = null; sortDirection.value = 'asc'; }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _renderSlot(_ctx.$slots, "title")
      ]),
      _cache[1] || (_cache[1] = _createTextVNode()),
      (_ctx.searchable)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              class: "data-table__search-input",
              placeholder: "Search..."
            }, null, 512), [
              [_vModelText, searchQuery.value]
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _cache[13] || (_cache[13] = _createTextVNode()),
    (_ctx.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[2] || (_cache[2] = [
          _createElementVNode("div", { class: "data-table__spinner" }, null, -1),
          _createTextVNode(),
          _createElementVNode("span", null, "Loading...", -1)
        ])))
      : (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createElementVNode("table", _hoisted_7, [
            _createElementVNode("thead", _hoisted_8, [
              _createElementVNode("tr", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
                  return (_openBlock(), _createElementBlock("th", {
                    key: column.field,
                    class: _normalizeClass({
                'data-table__th': true,
                'data-table__th--sortable': _ctx.sortable && column.sortable !== false,
                'data-table__th--sorted': sortColumn.value === column.field
              }),
                    style: _normalizeStyle({ width: column.width }),
                    onClick: ($event: any) => (handleSort(column.field))
                  }, [
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("span", null, _toDisplayString(column.label), 1),
                      _cache[3] || (_cache[3] = _createTextVNode()),
                      (_ctx.sortable && column.sortable !== false)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                            (sortColumn.value === column.field)
                              ? (_openBlock(), _createElementBlock("i", {
                                  key: 0,
                                  class: _normalizeClass({
                      'icon': true,
                      'icon-sort-down': sortDirection.value === 'desc',
                      'icon-sort-up': sortDirection.value === 'asc'
                    })
                                }, null, 2))
                              : (_openBlock(), _createElementBlock("i", _hoisted_12))
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ], 14, _hoisted_9))
                }), 128)),
                _cache[4] || (_cache[4] = _createTextVNode()),
                (_ctx.rowActions)
                  ? (_openBlock(), _createElementBlock("th", {
                      key: 0,
                      class: "data-table__th data-table__th--actions",
                      style: _normalizeStyle({ width: `${_ctx.rowActionsWidth}px` })
                    }, null, 4))
                  : _createCommentVNode("", true)
              ])
            ]),
            _cache[7] || (_cache[7] = _createTextVNode()),
            _createElementVNode("tbody", _hoisted_13, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedRows.value, (row, index) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: getRowKey(row, index),
                  class: "data-table__tr"
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
                    return (_openBlock(), _createElementBlock("td", {
                      key: column.field,
                      class: "data-table__td"
                    }, [
                      _renderSlot(_ctx.$slots, `cell:${column.field}`, {
                        row: row,
                        value: getNestedValue(row, column.field),
                        column: column
                      }, () => [
                        _createTextVNode(_toDisplayString(formatValue(row, column)), 1)
                      ])
                    ]))
                  }), 128)),
                  _cache[5] || (_cache[5] = _createTextVNode()),
                  (_ctx.rowActions)
                    ? (_openBlock(), _createElementBlock("td", _hoisted_14, [
                        _createVNode(ActionMenu, {
                          resource: row,
                          "data-testid": `data-table-${String(index)}-action-button`
                        }, null, 8, ["resource", "data-testid"])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              }), 128)),
              _cache[6] || (_cache[6] = _createTextVNode()),
              (paginatedRows.value.length === 0)
                ? (_openBlock(), _createElementBlock("tr", _hoisted_15, [
                    _createElementVNode("td", {
                      colspan: _ctx.rowActions ? _ctx.columns.length + 1 : _ctx.columns.length,
                      class: "data-table__td--empty"
                    }, [
                      _renderSlot(_ctx.$slots, "empty", {}, () => [
                        _createElementVNode("span", _hoisted_17, _toDisplayString(searchQuery.value ? 'No results found' : 'No data available'), 1)
                      ])
                    ], 8, _hoisted_16)
                  ]))
                : _createCommentVNode("", true)
            ])
          ])
        ])),
    _cache[14] || (_cache[14] = _createTextVNode()),
    (_ctx.paginated && !_ctx.loading && totalPages.value > 1)
      ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
          _createElementVNode("div", _hoisted_19, _toDisplayString(paginationInfo.value.start) + "-" + _toDisplayString(paginationInfo.value.end) + " of " + _toDisplayString(paginationInfo.value.total), 1),
          _cache[12] || (_cache[12] = _createTextVNode()),
          _createElementVNode("div", _hoisted_20, [
            _createElementVNode("button", {
              class: "data-table__pagination-btn",
              disabled: currentPage.value === 1,
              onClick: prevPage
            }, _cache[8] || (_cache[8] = [
              _createElementVNode("i", { class: "icon icon-chevron-left" }, null, -1)
            ]), 8, _hoisted_21),
            _cache[10] || (_cache[10] = _createTextVNode()),
            _createElementVNode("span", _hoisted_22, _toDisplayString(currentPage.value) + " / " + _toDisplayString(totalPages.value), 1),
            _cache[11] || (_cache[11] = _createTextVNode()),
            _createElementVNode("button", {
              class: "data-table__pagination-btn",
              disabled: currentPage.value === totalPages.value,
              onClick: nextPage
            }, _cache[9] || (_cache[9] = [
              _createElementVNode("i", { class: "icon icon-chevron-right" }, null, -1)
            ]), 8, _hoisted_23)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})